import React from "react";
import { Building2, Calendar, CalendarDays, MapPin, Pin, Search } from "lucide-react";
import MultiSelect from "../../../Component/CommonComponent/MultiSelect";

const PERIOD_OPTIONS = [
  { label: "Monthly", value: "MONTHLY" },
  { label: "Quarterly", value: "QUARTERLY" },
  { label: "Half Yearly", value: "HALFYEARLY" },
  { label: "Yearly", value: "YEARLY" },
];

/**
 * Renders the filter bar: KPI search, financial year, location, module and
 * period aggregation. All filtering is client-side except the financial
 * year, which drives the data fetch.
 *
 * @param {Object} props
 * @param {string} props.searchText - Current KPI search text.
 * @param {Function} props.onSearchChange - Receives the new search text.
 * @param {Array<Object>} props.financialYear - Financial year options.
 * @param {?number} props.financialYearId - Selected financial year id.
 * @param {Function} props.onFinancialYearChange - Receives the new FY id.
 * @param {Array<{value: string, label: string}>} props.locations - Location options.
 * @param {string[]} props.selectedLocationIds - Selected location values.
 * @param {Function} props.onLocationsChange - Receives the new location values.
 * @param {Array<{value: string, label: string}>} props.subLocations - Sub-location
 *   options for the current location selection.
 * @param {boolean} props.hasSubLocations - Whether the company has any
 *   sub-locations at all; keeps the control mounted (rather than flickering in
 *   and out) even when the current selection exposes none.
 * @param {string[]} props.selectedSubLocationIds - Selected sub-location values.
 * @param {Function} props.onSubLocationsChange - Receives the new sub-location values.
 * @param {Array<{value: number, label: string}>} props.moduleOptions - Module options.
 * @param {number[]} props.selectedModules - Selected module ids.
 * @param {Function} props.onModulesChange - Receives the new module ids.
 * @param {string} props.periodAggregation - Selected period aggregation.
 * @param {Function} props.onPeriodAggregationChange - Receives the new aggregation.
 * @returns {JSX.Element} The filter bar card.
 */
const QuickViewFilters = ({
  searchText,
  onSearchChange,
  financialYear,
  financialYearId,
  onFinancialYearChange,
  locations,
  selectedLocationIds,
  onLocationsChange,
  subLocations,
  hasSubLocations,
  selectedSubLocationIds,
  onSubLocationsChange,
  moduleOptions,
  selectedModules,
  onModulesChange,
  periodAggregation,
  onPeriodAggregationChange,
}) => (
  <div className="qv-card qv-filters">
    <div className="qv-search">
      <Search className="qv-search__icon" size={15} aria-hidden="true" />
      <input
        type="text"
        className="qv-search__input"
        value={searchText}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search KPI title…"
      />
    </div>

    <div className="qv-filters__controls">
      <div className="qv-field">
        <label className="qv-field__label">
          <Calendar size={14} aria-hidden="true" />
          Financial Year
        </label>
        <select
          className="qv-select"
          value={financialYearId || ""}
          onChange={(e) => onFinancialYearChange(parseInt(e.target.value, 10))}
        >
          {financialYear?.map((item) => (
            <option key={item.id} value={item.id}>
              {item.financial_year_value}
            </option>
          ))}
        </select>
      </div>

      <MultiSelect
        options={locations}
        selectedValues={selectedLocationIds}
        onChange={onLocationsChange}
        placeholder="Select Location"
        label="Location"
        icon={<MapPin size={14} aria-hidden="true" />}
        autoSelectAll
        containerStyle={{ minWidth: 0, width: "100%" }}
        closeOnScroll
      />

      {hasSubLocations && (
        <MultiSelect
          options={subLocations}
          selectedValues={selectedSubLocationIds}
          onChange={onSubLocationsChange}
          placeholder={subLocations.length > 0 ? "Select Sub Location" : "No sub locations"}
          label="Sub Location"
          icon={<Pin size={14} aria-hidden="true" />}
          autoSelectAll={false}
          containerStyle={{ minWidth: 0, width: "100%" }}
          closeOnScroll
        />
      )}

      {moduleOptions.length > 0 && (
        <MultiSelect
          options={moduleOptions}
          selectedValues={selectedModules}
          onChange={onModulesChange}
          placeholder="Select Module"
          label="Module"
          icon={<Building2 size={14} aria-hidden="true" />}
          autoSelectAll
          containerStyle={{ minWidth: 0, width: "100%" }}
          closeOnScroll
        />
      )}

      <div className="qv-field">
        <label className="qv-field__label">
          <CalendarDays size={14} aria-hidden="true" />
          Period
        </label>
        <select
          className="qv-select"
          value={periodAggregation}
          onChange={(e) => onPeriodAggregationChange(e.target.value)}
        >
          {PERIOD_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  </div>
);

export default QuickViewFilters;
